// Payout Accounting console (admin-portal, PayGate Phase 4): the platform view of the
// double-entry payout journal. Operators manage the chart of accounts (platform defaults +
// per-tenant overrides), post/refresh a batch's journal from the authoritative payout ledger
// (idempotent), inspect the resulting balanced entries, and read a tenant trial balance.
// All data comes from paygate-service via the gateway.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Form, Input, Modal, Row, Space, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, PlusOutlined, CalculatorOutlined } from '@ant-design/icons';
import * as api from '../../services/accountingService';

const { Title, Text } = Typography;

// Minor units → grouped display (currency shown separately per line/row).
const fmt = (n: number) => (n ?? 0).toLocaleString();

const eventTag = (e: string) => {
  const map: Record<string, string> = { submitted: 'blue', settled: 'green', reversed: 'orange', partner_settled: 'purple' };
  return <Tag color={map[e] || 'default'}>{e}</Tag>;
};

export const PaymentAccountingPage: React.FC = () => {
  const [tenantId, setTenantId] = useState('');
  const [chart, setChart] = useState<api.Account[]>([]);
  const [loadingChart, setLoadingChart] = useState(false);

  const [accForm] = Form.useForm();
  const [accOpen, setAccOpen] = useState(false);

  const [batchId, setBatchId] = useState('');
  const [entries, setEntries] = useState<api.JournalEntry[]>([]);
  const [journalLoading, setJournalLoading] = useState(false);

  const [tbTenant, setTbTenant] = useState('');
  const [tb, setTb] = useState<api.TrialBalanceRow[]>([]);
  const [tbLoading, setTbLoading] = useState(false);

  const loadChart = useCallback(async () => {
    setLoadingChart(true);
    try {
      setChart(await api.listChart(tenantId || undefined));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load chart of accounts');
    } finally {
      setLoadingChart(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  const submitAccount = async () => {
    try {
      const v = await accForm.validateFields();
      await api.upsertAccount({
        tenant_id: v.tenant_id || null,
        account_key: v.account_key,
        gl_code: v.gl_code,
        name: v.name,
        account_type: v.account_type || 'asset',
        normal_balance: v.normal_balance || 'debit',
      });
      message.success('Chart account saved');
      setAccOpen(false);
      accForm.resetFields();
      await loadChart();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Could not save account');
    }
  };

  const post = async () => {
    if (!batchId.trim()) return message.warning('Enter a batch ID');
    try {
      setJournalLoading(true);
      const r = await api.postBatch(batchId.trim());
      message.success(`Posted ${r.entries_posted} new entr${r.entries_posted === 1 ? 'y' : 'ies'} across ${r.items} item(s)`);
      setEntries(await api.batchJournal(batchId.trim()));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not post batch journal');
    } finally {
      setJournalLoading(false);
    }
  };

  const loadJournal = async () => {
    if (!batchId.trim()) return message.warning('Enter a batch ID');
    try {
      setJournalLoading(true);
      setEntries(await api.batchJournal(batchId.trim()));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load journal');
    } finally {
      setJournalLoading(false);
    }
  };

  const loadTrialBalance = async () => {
    if (!tbTenant.trim()) return message.warning('Enter a tenant ID');
    try {
      setTbLoading(true);
      setTb(await api.trialBalance(tbTenant.trim()));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load trial balance');
    } finally {
      setTbLoading(false);
    }
  };

  const tbTotals = tb.reduce((acc, r) => ({ debit: acc.debit + r.debit, credit: acc.credit + r.credit }), { debit: 0, credit: 0 });
  const balanced = tbTotals.debit === tbTotals.credit;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>Payout Accounting</Title>
      <Text type="secondary">Double-entry payout journal, chart of accounts and trial balance.</Text>

      <Alert
        type="info"
        showIcon
        style={{ margin: '16px 0' }}
        message="Journal is a projection of the payout ledger"
        description="Posting reads a batch's authoritative payout items and writes balanced entries idempotently — safe to re-run as items progress (submitted → settled / reversed). Cash entries exist only once funds actually left the bank."
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title="Chart of accounts"
            loading={loadingChart}
            extra={
              <Space>
                <Input
                  allowClear placeholder="Tenant ID (blank = platform defaults)" value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)} style={{ width: 300 }}
                />
                <Button icon={<ReloadOutlined />} onClick={loadChart}>Refresh</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAccOpen(true)}>Set account</Button>
              </Space>
            }
          >
            {chart.length === 0 ? (
              <Empty description="No accounts" />
            ) : (
              <Table
                size="small" pagination={false} rowKey="account_key" dataSource={chart}
                columns={[
                  { title: 'Key', dataIndex: 'account_key', render: (v) => <Text code>{v}</Text> },
                  { title: 'GL code', dataIndex: 'gl_code', render: (v) => <Text strong>{v}</Text> },
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Type', dataIndex: 'account_type' },
                  { title: 'Normal', dataIndex: 'normal_balance' },
                  {
                    title: 'Source', dataIndex: 'is_override',
                    render: (o: boolean) => (o ? <Tag color="blue">Tenant override</Tag> : <Tag>Platform default</Tag>),
                  },
                ]}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            title="Batch journal"
            extra={
              <Space>
                <Input placeholder="Batch ID (UUID)" value={batchId} onChange={(e) => setBatchId(e.target.value)} style={{ width: 300 }} />
                <Button onClick={loadJournal} loading={journalLoading}>View</Button>
                <Button type="primary" onClick={post} loading={journalLoading}>Post / refresh</Button>
              </Space>
            }
          >
            {entries.length === 0 ? (
              <Empty description="Enter a batch ID and view or post its journal" />
            ) : (
              <Table
                size="small" pagination={false} rowKey="id" dataSource={entries}
                columns={[
                  { title: 'Event', dataIndex: 'event', render: (e) => eventTag(e) },
                  { title: 'When', dataIndex: 'occurred_at', render: (v) => fmtDateTime(v) },
                  { title: 'Amount', dataIndex: 'amount', render: (a, r) => `${fmt(a)} ${r.currency}` },
                  {
                    title: 'Lines', dataIndex: 'lines',
                    render: (lines: api.JournalLine[]) => (
                      <Space direction="vertical" size={0}>
                        {lines.map((l, i) => (
                          <span key={i}>
                            <Tag color={l.direction === 'debit' ? 'geekblue' : 'gold'}>{l.direction === 'debit' ? 'DR' : 'CR'}</Tag>
                            <Text code>{l.gl_code}</Text> {l.account_key} — {fmt(l.amount)} {l.currency}
                          </span>
                        ))}
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title="Trial balance"
            extra={<CalculatorOutlined />}
          >
            <Space style={{ marginBottom: 12 }}>
              <Input placeholder="Tenant ID (UUID)" value={tbTenant} onChange={(e) => setTbTenant(e.target.value)} style={{ width: 260 }} />
              <Button type="primary" onClick={loadTrialBalance} loading={tbLoading}>Run</Button>
            </Space>
            {tb.length === 0 ? (
              <Empty description="Enter a tenant ID to compute its trial balance" />
            ) : (
              <>
                <Table
                  size="small" pagination={false} rowKey="gl_code" dataSource={tb}
                  columns={[
                    { title: 'GL', dataIndex: 'gl_code', render: (v) => <Text strong>{v}</Text> },
                    { title: 'Account', dataIndex: 'account_key' },
                    { title: 'Debit', dataIndex: 'debit', align: 'right', render: fmt },
                    { title: 'Credit', dataIndex: 'credit', align: 'right', render: fmt },
                    { title: 'Balance', dataIndex: 'balance', align: 'right', render: fmt },
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}><Text strong>Total</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><Text strong>{fmt(tbTotals.debit)}</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><Text strong>{fmt(tbTotals.credit)}</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        {balanced ? <Tag color="green">Balanced</Tag> : <Tag color="red">Off by {fmt(Math.abs(tbTotals.debit - tbTotals.credit))}</Tag>}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="Set chart account" open={accOpen} onOk={submitAccount} onCancel={() => setAccOpen(false)} okText="Save">
        <Form form={accForm} layout="vertical" initialValues={{ account_type: 'asset', normal_balance: 'debit' }}>
          <Form.Item name="tenant_id" label="Tenant ID (blank = platform default)">
            <Input placeholder="UUID" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="account_key" label="Account key" rules={[{ required: true }]}>
              <Input placeholder="e.g. settlement_bank" /></Form.Item></Col>
            <Col span={12}><Form.Item name="gl_code" label="GL code" rules={[{ required: true }]}>
              <Input placeholder="e.g. 1000" /></Form.Item></Col>
          </Row>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Settlement Bank" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="account_type" label="Type"><Input placeholder="asset | liability | expense" /></Form.Item></Col>
            <Col span={12}><Form.Item name="normal_balance" label="Normal balance"><Input placeholder="debit | credit" /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentAccountingPage;
